import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import type { ApiError, ScanOutcome } from '../api';
import { endpoints, when } from '../api';
import { feedback, keepAwake, startCamera } from '../camera';
import { isDuplicateScan, useHidScannerInput } from '../hid';
import type { LastScan } from '../hid';
import { decideOffline, offlineAdmissionAllowed } from '../offline';
import { clockOffsetMs, useScannerContext, useSelection } from '../session';
import { useSyncLoop } from '../sync';
import { Button } from '../ui';
import { dateWords } from '../terms';

/**
 * The gate (docs/scanner.md §3): camera or hardware scanner, a keypad for
 * the short code, and a result that fills the screen with a color, an
 * icon, a label, a tone and a vibration. Online decisions come from the
 * API; when it cannot be reached the bundle decides and the scan is queued.
 */

const LABELS: Record<string, string> = {
  VALID: 'Admit',
  MANUAL_OVERRIDE: 'Admitted by override',
  OFFLINE_ACCEPTED: 'Admit',
  RE_SCAN: 'Already admitted here',
  NOT_YET_VALID: 'Not yet',
  OFFLINE_CONFLICT: 'Offline conflict',
  ALREADY_SCANNED: 'Already scanned',
  INVALID: 'Invalid',
  WRONG_EVENT: 'Wrong event',
  get WRONG_PERFORMANCE() {
    return `Wrong ${dateWords().one}`;
  },
  WRONG_ACCESS_ZONE: 'Wrong gate',
  EXPIRED: 'Admission closed',
  REFUNDED: 'Refunded',
  REVOKED: 'Revoked',
  TRANSFERRED: 'Superseded',
};
const ICONS: Record<'GREEN' | 'YELLOW' | 'RED', string> = { GREEN: '✓', YELLOW: '!', RED: '✕' };
/** Why someone came through outside their entry time. Tapped, not typed: it is a doorway. */
const ARRIVAL_REASONS = ['Queue held them up', 'Came early, house had room', 'Guest misread their time', 'Manager waved them through'];
const KEYS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '2', '3', '4', '5', '6', '7', '8', '9'];

export default function ScanPage() {
  const navigate = useNavigate();
  const { selection } = useSelection();
  const context = useScannerContext();
  const sync = useSyncLoop(Boolean(selection));
  const [result, setResult] = useState<ScanOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [override, setOverride] = useState<{ scanId: string; pin: string; reason: string; error: string | null } | null>(null);
  const [counters, setCounters] = useState<ScanOutcome['counters'] | null>(null);
  const [arrival, setArrival] = useState<{ scanId: string; busy: boolean; error: string | null } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastRef = useRef<LastScan | null>(null);
  const dismissTimer = useRef<number | null>(null);

  const performances = (context.data?.performances ?? []).filter((p) => selection?.performance_ids.includes(p.id));
  const title = performances.map((p) => p.title).join(' + ') || 'Scanning';
  const timezone = performances[0]?.timezone ?? 'America/Chicago';

  const show = useCallback((outcome: ScanOutcome) => {
    if (dismissTimer.current) window.clearTimeout(dismissTimer.current);
    setResult(outcome);
    setCounters(outcome.counters);
    feedback(outcome.color);
    // A green result clears itself, unless it arrived outside its entry time: that
    // one needs a door decision, so it waits to be dismissed (D-86, PART 35).
    const unusual = outcome.timed_entry && outcome.timed_entry.arrival !== 'ON_TIME';
    if (outcome.color === 'GREEN' && !unusual) dismissTimer.current = window.setTimeout(() => setResult(null), 1200);
  }, []);

  const handle = useCallback(
    async (raw: string, inputMethod: 'CAMERA' | 'HID_SCANNER' | 'MANUAL_CODE') => {
      if (!selection || busy) return;
      const now = Date.now();
      if (isDuplicateScan(lastRef.current, raw, now)) return;
      lastRef.current = { raw, at: now };
      setBusy(true);
      const deviceTimestamp = new Date(now).toISOString();
      try {
        const outcome = await endpoints.scan({ scan_id: crypto.randomUUID(), raw, performance_ids: selection.performance_ids, gate_id: selection.gate_id, direction: 'ENTRY', input_method: inputMethod, device_timestamp: deviceTimestamp });
        show(outcome);
      } catch (cause) {
        const error = cause as ApiError;
        if (error.isOffline && !(await offlineAdmissionAllowed(selection.performance_ids))) {
          // Switched off in Admin (D-329): nobody is admitted on this device's word alone.
          show({ scan_id: '', result: 'INVALID', color: 'RED', counts_as_admission: false, reason_code: 'OFFLINE_ADMISSION_OFF', reason_text: 'OFFLINE — NOT ADMITTING. Admitting without a network is switched off. Send the guest to the box office, or wait for the network.', ticket: null, previous: null, counters: counters ?? { gate_admitted: 0, performance_admitted: 0, performance_live_tickets: 0 }, offline: true });
        } else if (error.isOffline) {
          const offline = await decideOffline({ raw, performance_ids: selection.performance_ids, gate_id: selection.gate_id, gate_name: selection.gate_name, direction: 'ENTRY', input_method: inputMethod, now: deviceTimestamp, clock_offset_ms: clockOffsetMs() });
          if (offline) show(offline);
          else show({ scan_id: '', result: 'INVALID', color: 'RED', counts_as_admission: false, reason_code: 'NO_BUNDLE', reason_text: 'OFFLINE — NO BUNDLE. Download the bundle when the network returns, or check the ticket at the box office.', ticket: null, previous: null, counters: counters ?? { gate_admitted: 0, performance_admitted: 0, performance_live_tickets: 0 }, offline: true });
        } else if (error.isUnauthenticated) void navigate('/login', { replace: true });
        else show({ scan_id: '', result: 'INVALID', color: 'RED', counts_as_admission: false, reason_code: error.code, reason_text: error.message, ticket: null, previous: null, counters: counters ?? { gate_admitted: 0, performance_admitted: 0, performance_live_tickets: 0 } });
      } finally {
        setBusy(false);
      }
    },
    [selection, busy, show, counters, navigate],
  );

  useHidScannerInput((raw) => void handle(raw, 'HID_SCANNER'), Boolean(selection) && manual === null && override === null);

  // Camera and wake lock for the life of the page.
  useEffect(() => {
    if (!selection || selection.input !== 'CAMERA') return;
    const video = videoRef.current;
    if (!video) return;
    let session: { stop(): void } | null = null;
    let release: (() => void) | null = null;
    startCamera(video, (raw) => void handle(raw, 'CAMERA'))
      .then((s) => {
        session = s;
      })
      .catch((cause: Error) => setCameraError(cause.message));
    void keepAwake().then((r) => {
      release = r;
    });
    return () => {
      session?.stop();
      release?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection?.input]);

  if (!selection) return <Navigate to="/" replace />;

  const submitOverride = async () => {
    if (!override || override.pin.length !== 6) return;
    try {
      const outcome = await endpoints.override(override.scanId, { manager_pin: override.pin, reason: override.reason || 'Manager override at the gate' });
      setOverride(null);
      show(outcome);
    } catch (cause) {
      setOverride({ ...override, pin: '', error: (cause as ApiError).message });
    }
  };

  return (
    <div className="safe-top safe-bottom relative flex min-h-full flex-col">
      <header className="flex items-center gap-3 px-4 py-2 text-sm">
        <button type="button" onClick={() => void navigate('/')} className="touch rounded-lg px-2 text-white/70">
          ‹ Setup
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{title}</p>
          <p className="truncate text-xs text-white/60">
            {selection.gate_name ?? 'Any gate'} · {sync.online ? 'online' : 'OFFLINE'}
            {sync.pending > 0 ? ` · ${sync.pending} to sync` : ''}
          </p>
        </div>
        {counters ? (
          <div className="tabular text-right text-xs text-white/70">
            <p className="text-lg font-semibold text-white">{counters.performance_admitted}</p>
            <p>of {counters.performance_live_tickets} in</p>
          </div>
        ) : null}
      </header>

      <main className="relative flex-1 bg-black">
        {selection.input === 'CAMERA' ? (
          <>
            <video ref={videoRef} muted playsInline className="absolute inset-0 h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="h-56 w-56 rounded-2xl border-4 border-white/60" />
            </div>
            {cameraError ? <p className="absolute inset-x-0 bottom-4 px-4 text-center text-sm text-white/80">Camera unavailable: {cameraError}. Use the keypad or a hardware scanner.</p> : null}
          </>
        ) : (
          <div className="grid h-full place-items-center p-8 text-center text-white/70">
            <p>Ready for the hardware scanner. Point it at the code and pull the trigger.</p>
          </div>
        )}
        {busy ? <div className="absolute inset-x-0 top-0 h-1 animate-pulse bg-gold" /> : null}
      </main>

      <footer className="flex gap-2 p-3">
        <Button className="flex-1" onClick={() => setManual('')}>
          Type a code
        </Button>
        {sync.pending > 0 ? (
          <Button onClick={() => void sync.syncNow()} className="px-4">
            Sync {sync.pending}
          </Button>
        ) : null}
      </footer>
      {sync.conflicts.length > 0 ? <p className="bg-warn px-4 py-2 text-sm text-shell-900">Offline conflicts after sync: {sync.conflicts.map((c) => c.seat_label ?? c.scan_id.slice(0, 8)).join(', ')}. Admitted elsewhere first.</p> : null}
      {sync.wiped.length > 0 ? <p className="bg-bad px-4 py-2 text-sm text-white">A bundle expired or was revoked and was removed from this device.</p> : null}

      {result ? (
        <div
          role="status"
          aria-live="assertive"
          onClick={() => setResult(null)}
          className={`absolute inset-0 z-20 flex flex-col items-center justify-center p-6 text-center ${result.color === 'GREEN' ? 'bg-ok' : result.color === 'YELLOW' ? 'bg-warn text-shell-900' : 'bg-bad'}`}
        >
          <p className="text-8xl font-black" aria-hidden="true">
            {ICONS[result.color]}
          </p>
          <p className="mt-2 text-3xl font-bold">{LABELS[result.result] ?? result.result}</p>
          {result.offline && result.color === 'GREEN' ? <p className="mt-1 rounded-full bg-black/30 px-3 py-1 text-sm font-semibold tracking-wide uppercase">Offline · provisional</p> : null}
          {result.ticket ? (
            <div className="mt-6 text-xl">
              {result.season_pass ? <p className="text-sm font-semibold tracking-wide uppercase opacity-90">Season pass · {result.season_pass.label}</p> : null}
              <p className="font-semibold">{result.ticket.seat_label}</p>
              <p>{result.ticket.ticket_type ?? ''}</p>
              {result.ticket.holder_name ? <p className="text-lg opacity-90">{result.ticket.holder_name}</p> : null}
            </div>
          ) : result.season_pass ? (
            // A pass with no ticket yet: refused, or let in offline with its ticket made on sync (D-373).
            <div className="mt-6 text-xl">
              <p className="font-semibold">Season pass</p>
              <p>{result.season_pass.label}</p>
              {result.offline && result.color === 'GREEN' ? <p className="text-base opacity-90">General admission; the ticket is made when this device syncs</p> : null}
            </div>
          ) : null}
          {result.timed_entry ? (
            <div className="mt-4 rounded-xl bg-black/25 px-4 py-3">
              <p className="text-lg font-semibold">Entry {when(result.timed_entry.starts_at, timezone, 'time')}</p>
              <p className="text-base">
                {result.timed_entry.arrival === 'ON_TIME'
                  ? 'Arrived on time'
                  : result.timed_entry.arrival === 'EARLY'
                    ? `${result.timed_entry.minutes_from_window} min early`
                    : `${result.timed_entry.minutes_from_window} min late`}
              </p>
            </div>
          ) : null}
          {result.reason_text && result.color !== 'GREEN' ? <p className="mt-4 max-w-md text-lg">{result.reason_text}</p> : null}
          {result.previous ? (
            <p className="mt-2 text-base opacity-90">
              Previous scan {when(result.previous.at, timezone, 'time')} · {result.previous.gate ?? 'gate'} · {result.previous.device}
              {result.previous.staff ? ` · ${result.previous.staff}` : ''}
            </p>
          ) : null}
          {result.timed_entry && result.timed_entry.arrival !== 'ON_TIME' && result.counts_as_admission && result.scan_id && !result.offline && result.reason_code !== 'ARRIVAL_OVERRIDE' ? (
            <Button
              className="mt-6 bg-white text-shell-900"
              onClick={(e) => {
                e.stopPropagation();
                setArrival({ scanId: result.scan_id, busy: false, error: null });
              }}
            >
              Record why they were let in
            </Button>
          ) : null}
          {/*
            The variant, not a hand-painted pair of colors (D-284).

            The override button below passed `bg-white text-shell-900` to a
            Button whose default variant already sets `text-white`. Both
            utilities are emitted and the winner is decided by the order of the
            generated stylesheet, not by the order of the class list -- so it
            came out white on white, and the only override at the gate was a
            blank rectangle. `primary` is a defined, contrast-checked pair.
          */}
          {result.color === 'RED' && result.ticket && context.data?.can_override !== undefined && result.scan_id && !result.offline ? (
            <Button
              variant="primary"
              className="mt-8"
              onClick={(e) => {
                e.stopPropagation();
                setOverride({ scanId: result.scan_id, pin: '', reason: '', error: null });
              }}
            >
              Manager override
            </Button>
          ) : null}
          {result.color !== 'GREEN' ? <p className="mt-6 text-sm opacity-80">Tap or scan the next ticket</p> : null}
        </div>
      ) : null}

      {manual !== null ? (
        <div className="absolute inset-0 z-30 flex flex-col bg-shell-900 p-4">
          <p className="text-sm text-white/60">Ticket code</p>
          <p className="tabular mt-1 min-h-12 text-center text-3xl font-semibold tracking-[0.3em]">{manual || ' '}</p>
          <div className="mt-4 grid grid-cols-6 gap-2">
            {KEYS.map((k) => (
              <button key={k} type="button" onClick={() => setManual((m) => (m ?? '') + k)} className="h-14 rounded-lg bg-shell-700 text-xl font-semibold active:bg-shell-500">
                {k}
              </button>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => setManual((m) => (m ?? '').slice(0, -1))}>⌫</Button>
            <Button variant="ghost" onClick={() => setManual(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              disabled={(manual ?? '').length < 6}
              onClick={() => {
                const code = manual ?? '';
                setManual(null);
                void handle(code, 'MANUAL_CODE');
              }}
            >
              Check
            </Button>
          </div>
        </div>
      ) : null}

      {arrival ? (
        <div className="absolute inset-0 z-40 flex flex-col bg-shell-900 p-5" role="dialog" aria-modal="true" aria-label="Why this guest was admitted outside their entry time">
          <h2 className="text-xl font-semibold">Outside their entry time</h2>
          <p className="mt-1 text-sm text-white/60">They are already admitted. Tell us why, so the night&rsquo;s numbers make sense afterward. It is recorded against you.</p>
          {arrival.error ? <p className="mt-3 rounded-lg bg-bad/30 px-3 py-2 text-sm">{arrival.error}</p> : null}
          <div className="mt-5 grid gap-2">
            {ARRIVAL_REASONS.map((reason) => (
              <Button
                key={reason}
                className="justify-start"
                disabled={arrival.busy}
                onClick={() => {
                  setArrival({ ...arrival, busy: true, error: null });
                  void endpoints
                    .arrivalOverride(arrival.scanId, { reason })
                    .then(() => {
                      setArrival(null);
                      setResult(null);
                    })
                    .catch((cause: ApiError) => setArrival({ ...arrival, busy: false, error: cause.message }));
                }}
              >
                {reason}
              </Button>
            ))}
          </div>
          <Button variant="ghost" className="mt-6" onClick={() => setArrival(null)}>
            Not now
          </Button>
        </div>
      ) : null}

      {override ? (
        <div className="absolute inset-0 z-40 flex flex-col bg-shell-900 p-5" role="dialog" aria-modal="true">
          <h2 className="text-xl font-semibold">Manager override</h2>
          <p className="mt-1 text-sm text-white/60">A manager with scan.override enters their PIN to admit this guest. It is recorded against them.</p>
          {override.error ? <p className="mt-3 rounded-lg bg-bad/30 px-3 py-2 text-sm">{override.error}</p> : null}
          <input value={override.reason} onChange={(e) => setOverride({ ...override, reason: e.target.value })} placeholder="Reason (e.g. duplicate at other gate, guest present)" className="touch mt-4 w-full rounded-xl border border-shell-500 bg-shell-800 px-4" />
          <div className="mt-4 flex justify-center gap-3" aria-label="Manager PIN">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <span key={i} className={`h-4 w-4 rounded-full ${i < override.pin.length ? 'bg-gold' : 'bg-shell-700'}`} />
            ))}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key, i) => (
              <button
                key={i}
                type="button"
                disabled={key === ''}
                onClick={() => {
                  if (key === '⌫') setOverride({ ...override, pin: override.pin.slice(0, -1) });
                  else if (override.pin.length < 6) {
                    const pin = override.pin + key;
                    setOverride({ ...override, pin });
                    if (pin.length === 6) void submitOverride();
                  }
                }}
                className={`h-16 rounded-xl text-2xl font-semibold ${key === '' ? 'invisible' : 'bg-shell-700 active:bg-shell-500'}`}
              >
                {key}
              </button>
            ))}
          </div>
          <Button variant="ghost" className="mt-4" onClick={() => setOverride(null)}>
            Cancel
          </Button>
        </div>
      ) : null}
    </div>
  );
}
